import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import contractApi from "../../../APIs/contract";
import jobsApi from "../../../APIs/jobs";
import proposalsApi from "../../../APIs/proposals";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import {
  ProfessionalInformation,
  ProfessionalNote,
  ProjectTimeline,
  TechnicalSpecification,
  WorkApproach,
} from "../../../components/Proposals/ProposalForTechnician";
import escConstants from "../../../constants";
import {
  Button,
  Divider,
  Loader,
  NavigateBack,
  Tag,
  Typography,
} from "../../../designComponents";
import { RootState } from "../../../store/selectors";
import { requestAPI } from "../../../utils";
import { ViewJobContainerStyled } from "../../BrowseJobs/BrowseJobDetails/style";
import { ActionsContainer, ViewProposalContainerStyled } from "./style";

const ProposalForTechnician = () => {
  const [loading, setLoading] = useState(false);
  const { proposalId, individualProposalId } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [IsProposalModal, setIsProposalModal] = useState(false);
  const [IsProposalRejectedModal, setIsProposalRejectedModal] = useState(false);

  const navigate = useNavigate();

  const proposalsListDetails = useSelector(
    (state: RootState) => state.proposalsListDetails,
  );

  const individualProposalsDetails = useSelector(
    (state: RootState) => state.individualProposalsDetails,
  );
  const jobDetails = useSelector((state: RootState) => state.jobAndProposal);
  const getProposalDetails = async (individualId: string) => {
    try {
      setLoading(true);
      await requestAPI(proposalsApi.getIndividualProposalById(individualId));
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (individualProposalId) {
      getProposalDetails(individualProposalId);
    }
  }, [individualProposalId]);

  const proposalStatus = individualProposalsDetails?.proposalStatus;

  const clientList =
    Array.isArray(individualProposalsDetails.pastClients) &&
    individualProposalsDetails.pastClients?.length > 0
      ? individualProposalsDetails.pastClients
      : [];

  const handleAcceptProposal = async () => {
    try {
      if (proposalId && individualProposalId) {
        setLoading(true);
        await requestAPI(
          proposalsApi.updateProposalStatus(proposalId, individualProposalId, {
            status: "ACCEPT",
          }),
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      toast(escConstants.toastMessages.proposalAccepted);
      setIsProposalModal(false);
      if (individualProposalId) {
        getProposalDetails(individualProposalId);
      }
    }
  };
  const handleDeclineProposal = async () => {
    try {
      if (proposalId && individualProposalId) {
        setLoading(true);
        await requestAPI(
          proposalsApi.updateProposalStatus(proposalId, individualProposalId, {
            status: "DECLINE",
          }),
        );
      }
      setIsProposalRejectedModal(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      toast(escConstants.toastMessages.proposalDeclined);
      if (individualProposalId) {
        getProposalDetails(individualProposalId);
      }
    }
  };

  const getAcceptedProposalJobDetails = async (id: string) => {
    try {
      await requestAPI(jobsApi.getJobAndProposalById(id));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (proposalId) {
      getAcceptedProposalJobDetails(proposalId);
    }
  }, [proposalId]);

  const handleCreateContract = async () => {
    try {
      if (typeof jobDetails?.technicianJobId === "string") {
        return;
      }
      setLoading(true);
      await requestAPI(
        contractApi.createContract({
          proposalId: individualProposalsDetails?._id,
          jobId: jobDetails?.technicianJobId?._id,
          startDate: individualProposalsDetails?.startingDate,
          endDate: individualProposalsDetails?.endDate,
          notes: individualProposalsDetails?.notes,
        }),
      );
      setLoading(false);
      setIsOpen(false);
      toast(escConstants.toastMessages.contractCreated);
      if (proposalId) {
        getAcceptedProposalJobDetails(proposalId);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };
  console.log(individualProposalsDetails, "details");
  const checkStatus = () => {
    if (
      individualProposalsDetails.proposalStatus === "ACCEPT" &&
      !jobDetails?.contract
    ) {
      return (
        <Button
          variant="secondary"
          size="sm"
          width="122px"
          onClick={() => setIsOpen(true)}
        >
          Create Contract
        </Button>
      );
    }

    if (
      jobDetails?.contract?.contractStatus === "PROPOSAL-CREATED" ||
      jobDetails?.contract?.contractStatus === "ACCEPT"
    ) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Tag
            type={
              jobDetails?.contract?.contractStatus === "PROPOSAL-CREATED"
                ? "warning"
                : jobDetails?.contract?.contractStatus === "ACCEPT"
                  ? "success"
                  : "caution"
            }
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            Contract{" "}
            {jobDetails.contract.contractStatus.charAt(0).toUpperCase() +
              jobDetails.contract.contractStatus.slice(1).toLowerCase()}
          </Tag>
          <Button
            variant="secondary"
            onClick={() => navigate(`/my-jobs/${proposalId}`)}
          >
            View Job
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {loading ? (
        <Loader
          type="spinner"
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        />
      ) : (
        <ViewJobContainerStyled>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              position: "sticky",
              top: "60px",
              zIndex: "999",
              paddingTop: "5px",
              background: "#fff",
            }}
          >
            <NavigateBack />
            {checkStatus()}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 40,
              paddingLeft: 30,
            }}
          >
            <Typography variant="title">
              {individualProposalsDetails.technicianJob?.jobTitle}
            </Typography>
            {proposalStatus === "ACCEPT" ? (
              jobDetails?.contract?.contractStatus === "PENDING" ? (
                <Tag type="caution">CONTRACT CREATED</Tag>
              ) : (
                <Tag type="success">ACCEPTED</Tag>
              )
            ) : null}
            {proposalStatus === "DECLINE" && <Tag type="danger">DECLINED</Tag>}
            {proposalStatus === "PROPOSAL-CREATED" && (
              <Tag type="warning">PENDING</Tag>
            )}
          </div>
          <Divider />
          <ViewProposalContainerStyled>
            <ProfessionalInformation
              technicianName={
                individualProposalsDetails?.createdBy?.fullName ||
                // individualProposalsDetails?.createdBy?.fullName ||
                "Default Name"
              }
              emailAddress={
                individualProposalsDetails?.createdBy?.emailAddress ||
                "Default Name"
              }
              avatar={individualProposalsDetails?.createdBy?.avatar || ""}
            />
            <Divider />
            <TechnicalSpecification
              specification={individualProposalsDetails?.specification}
              experience={individualProposalsDetails?.experience}
              numberOfExperience={
                individualProposalsDetails?.numberOfExperience
              }
              costEstimation={individualProposalsDetails?.cost}
              qualification={individualProposalsDetails?.qualifications}
              pastClient={clientList}
              pastProject={individualProposalsDetails?.pastProject || [""]}
            />
            <Divider />
            <WorkApproach
              workApproach={individualProposalsDetails?.workApproach}
              designApproach={individualProposalsDetails?.designApproach}
              riskAssesment={individualProposalsDetails?.riskAssessment}
            />
            <Divider />
            <ProjectTimeline
              startingDate={individualProposalsDetails?.startingDate || ""}
              endDate={individualProposalsDetails?.endDate || ""}
            />
            <Divider />
            <ProfessionalNote
              professionalNotes={individualProposalsDetails?.notes || ""}
            />
            <Divider />
            {proposalStatus === "PROPOSAL-CREATED" &&
            !proposalsListDetails?.acceptedProposal ? (
              <>
                <Typography variant="normal" fontColor="secondary" strong>
                  ACTIONS
                </Typography>
                <ActionsContainer>
                  <Button
                    variant="secondary"
                    onClick={() => setIsProposalModal(true)}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setIsProposalRejectedModal(true)}
                  >
                    Decline
                  </Button>
                </ActionsContainer>
              </>
            ) : (
              ""
            )}
          </ViewProposalContainerStyled>
          <ConfirmationModal
            title="Confirmation"
            message="Are you sure want to create contract?"
            cancelText="No"
            okText="Yes"
            isOpen={isOpen}
            onClose={() => {
              setIsOpen(false);
            }}
            onOk={handleCreateContract}
          />
          <ConfirmationModal
            title="Confirmation"
            message="Are you sure want to accept proposal?"
            cancelText="No"
            okText="Yes"
            isOpen={IsProposalModal}
            onClose={() => setIsProposalModal(false)}
            onOk={handleAcceptProposal}
          />
          <ConfirmationModal
            title="Confirmation"
            message="Are you sure want to reject proposal?"
            cancelText="No"
            okText="Yes"
            isOpen={IsProposalRejectedModal}
            onClose={() => setIsProposalRejectedModal(false)}
            onOk={handleDeclineProposal}
          />
        </ViewJobContainerStyled>
      )}
    </>
  );
};

export default ProposalForTechnician;
